import { useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';

import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded';
import {
  Box,
  Chip,
  Drawer,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';

import { CompactTablePagination, EmptyState, ErrorState, LoadingState } from '@/shared/ui';

import { usePublicationRunDetail } from '../../hooks';
import { formatDateTime, formatNumber } from '../../lib/format';
import {
  publicationRunOutcomeColor,
  publicationRunOutcomeLabel,
  publicationRunStatusColor,
  publicationRunStatusLabel,
  publicationRunTriggerLabel,
} from '../../model/options';

const OUTCOMES_PAGE_SIZE = 10;

/**
 * A run's per-product outcomes carry only a raw `productId` (no code/name — this contract has no
 * bulk-by-ids catalog lookup to resolve one, the same disclosed gap as `features/inventory`'s
 * `ProductIdCell`) — shown truncated + monospace with a copy affordance.
 */
function OutcomeProductId({ productId }: { productId: string }) {
  const { t } = useTranslation('catalog');
  const [copied, setCopied] = useState(false);
  const truncated = productId.length > 10 ? `${productId.slice(0, 8)}…` : productId;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(productId);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard API can be unavailable — the full id is still visible in the tooltip.
    }
  };

  return (
    <Stack direction="row" spacing={0.25} alignItems="center">
      <Tooltip title={productId}>
        <Typography variant="caption" sx={{ fontFamily: 'JetBrains Mono, monospace', whiteSpace: 'nowrap' }}>
          {truncated}
        </Typography>
      </Tooltip>
      <Tooltip title={copied ? t('publicationRunsPanel.detail.idCopied') : t('publicationRunsPanel.detail.idCopyAria')}>
        <IconButton size="small" aria-label={t('publicationRunsPanel.detail.idCopyAria')} onClick={handleCopy}>
          <ContentCopyRoundedIcon sx={{ fontSize: 14 }} />
        </IconButton>
      </Tooltip>
    </Stack>
  );
}

export interface PublicationRunDetailDrawerProps {
  open: boolean;
  onClose: () => void;
  catalogId: string;
  runId: string | null;
}

export function PublicationRunDetailDrawer({ open, onClose, catalogId, runId }: PublicationRunDetailDrawerProps) {
  const { t } = useTranslation('catalog');
  const [page, setPage] = useState(0);
  const detailQuery = usePublicationRunDetail(catalogId, runId ?? undefined, { enabled: open && runId !== null });

  useEffect(() => {
    setPage(0);
  }, [runId]);

  const detail = detailQuery.data;
  const outcomes = detail?.outcomes ?? [];
  const pageItems = outcomes.slice(page * OUTCOMES_PAGE_SIZE, page * OUTCOMES_PAGE_SIZE + OUTCOMES_PAGE_SIZE);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      slotProps={{ paper: { sx: { width: { xs: '100vw', sm: 640 }, display: 'flex', flexDirection: 'column' } } }}
    >
      <Box
        sx={{
          px: 2.5,
          py: 1.75,
          borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          flexShrink: 0,
          bgcolor: 'background.paper',
        }}
      >
        <HistoryRoundedIcon color="action" aria-hidden />
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600 }}>
            {t('publicationRunsPanel.detail.title')}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small" aria-label={t('common:action.close')}>
          <CloseRoundedIcon fontSize="small" />
        </IconButton>
      </Box>

      <Box sx={{ flex: 1, overflowY: 'auto', p: 2.5 }}>
        {detailQuery.isLoading ? (
          <LoadingState label={t('publicationRunsPanel.detail.loading')} />
        ) : detailQuery.isError ? (
          <ErrorState
            title={t('publicationRunsPanel.detail.loadError')}
            message={detailQuery.error?.message}
            onRetry={() => void detailQuery.refetch()}
          />
        ) : detail ? (
          <Stack spacing={2.5}>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
              <Chip size="small" variant="outlined" label={publicationRunTriggerLabel(t, detail.trigger)} />
              <Chip
                size="small"
                color={publicationRunStatusColor(detail.status)}
                label={publicationRunStatusLabel(t, detail.status)}
              />
              <Typography variant="body2" color="text.secondary">
                {formatDateTime(detail.startedAt)}
              </Typography>
            </Stack>

            <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
              <Typography variant="body2">
                {t('publicationRunsPanel.detail.evaluated', { count: formatNumber(detail.productsEvaluated) })}
              </Typography>
              <Typography variant="body2" color="success.main">
                {t('publicationRunsPanel.detail.included', { count: formatNumber(detail.productsIncluded) })}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('publicationRunsPanel.detail.excluded', { count: formatNumber(detail.productsExcluded) })}
              </Typography>
              <Typography variant="body2" color="warning.main">
                {t('publicationRunsPanel.detail.failedProfile', { count: formatNumber(detail.productsFailedProfile) })}
              </Typography>
            </Stack>

            {outcomes.length === 0 ? (
              <EmptyState
                title={t('publicationRunsPanel.detail.empty.title')}
                description={t('publicationRunsPanel.detail.empty.description')}
              />
            ) : (
              <Stack spacing={1.5}>
                <TableContainer>
                  <Table size="small" aria-label={t('publicationRunsPanel.detail.title')}>
                    <TableHead>
                      <TableRow>
                        <TableCell>{t('publicationRunsPanel.detail.table.product')}</TableCell>
                        <TableCell>{t('publicationRunsPanel.detail.table.outcome')}</TableCell>
                        <TableCell>{t('publicationRunsPanel.detail.table.details')}</TableCell>
                        <TableCell>{t('publicationRunsPanel.detail.table.recordedAt')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {pageItems.map((outcome, index) => (
                        <TableRow key={`${outcome.productId}-${index}`}>
                          <TableCell>
                            <OutcomeProductId productId={outcome.productId} />
                          </TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              variant="outlined"
                              color={publicationRunOutcomeColor(outcome.outcome)}
                              label={publicationRunOutcomeLabel(t, outcome.outcome)}
                            />
                          </TableCell>
                          <TableCell>
                            {outcome.missingRequiredFields.length > 0 ? (
                              <Typography variant="body2">{outcome.missingRequiredFields.join(', ')}</Typography>
                            ) : (
                              <Typography variant="body2" color="text.secondary">
                                —
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" noWrap>
                              {formatDateTime(outcome.createdAt)}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <CompactTablePagination
                  count={outcomes.length}
                  page={page}
                  rowsPerPage={OUTCOMES_PAGE_SIZE}
                  hideRowsPerPage
                  onPageChange={(_e, next) => setPage(next)}
                  onRowsPerPageChange={() => {}}
                />
              </Stack>
            )}
          </Stack>
        ) : null}
      </Box>
    </Drawer>
  );
}
